import React, { PureComponent, createElement } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { withStyles } from '@material-ui/core/styles'
import ButtonBase from '@material-ui/core/ButtonBase'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import CircularProgress from '@material-ui/core/CircularProgress'

const createColorStyle = (palette, main, dark) => ({
  '& $button': {
    borderColor: palette[main],
    color: palette[main],
  },
  '& $deleteButton': {
    backgroundColor: palette[main],
    color: palette.contrastText,
  },
  '& $button$buttonClickable': {
    '&:hover': {
      backgroundColor: palette[main],
      borderColor: palette[main],
      color: palette.contrastText,
    },
  },
  '&$rootActive': {
    '& $button': {
      backgroundColor: palette[main],
      color: palette.contrastText,
    },
    '& $button$buttonClickable': {
      '&:hover': {
        backgroundColor: palette[dark],
        borderColor: palette[dark],
      },
    },
    '& $deleteButton': {
      backgroundColor: palette[dark],
    }
  },
})

const styles = theme => ({
  root: {
    position: 'relative',
    display: 'inline-flex',
  },
  rootActive: {},
  rootFullWidth: {
    display: 'flex',
  },
  button: {
    width: '100%',
    height: '2rem',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 3,
    padding: `0 ${theme.spacing.unit * 2}px`,
    margin: 0,
    outline: 0,
    background: 'none',
    border: '1px solid',
    whiteSpace: 'nowrap',
  },
  buttonClickable: {
    cursor: 'pointer',
    transition: theme.transitions.create(['color', 'background-color', 'border-color']),
  },
  buttonRound: {
    minWidth: '2rem',
    borderRadius: '1rem',
  },
  buttonNoPadding: {
    padding: 0,
  },
  buttonChildren: {},
  buttonLeftSpace: {
    '&:not($buttonNoPadding)': {
      paddingLeft: theme.spacing.unit,
    },
    '& $buttonChildren': {
      marginLeft: theme.spacing.unit,
    }
  },
  buttonRightSpace: {
    '&:not($buttonNoPadding)': {
      paddingRight: theme.spacing.unit,
    },
    '& $buttonChildren': {
      marginRight: theme.spacing.unit,
    }
  },
  deleteButton: {
    padding: 0,
    position: 'absolute',
    top: -theme.spacing.unit,
    right: -theme.spacing.unit,
    width: theme.spacing.unit * 2,
    height: theme.spacing.unit * 2,
    transition: theme.transitions.create('background-color'),
  },
  deleteButtonIcon: {
    width: theme.spacing.unit * 1.5,
    height: theme.spacing.unit * 1.5,
  },
  primary: createColorStyle(theme.palette.primary, 'main', 'dark'),
  secondary: createColorStyle(theme.palette.secondary, 'light', 'main'),
})

export default
@withStyles(styles, { withTheme: true })
class Chip extends PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    children: PropTypes.any,
    active: PropTypes.bool,
    round: PropTypes.bool,
    icon: PropTypes.any,
    rightIcon: PropTypes.any,
    onDelete: PropTypes.func,
    onClick: PropTypes.func,
    className: PropTypes.any,
    disablePadding: PropTypes.bool,
    fullWidth: PropTypes.bool,
    color: PropTypes.oneOf(['primary', 'secondary']),
    pending: PropTypes.bool,
    clickable: PropTypes.bool,
    theme: PropTypes.object.isRequired,
  }

  static defaultProps = {
    color: 'secondary',
    clickable: true,
  }

  renderPending() {
    const { pending, theme } = this.props
    return !!pending && (
      <CircularProgress size={theme.spacing.unit * 2} color="inherit" />
    )
  }

  renderIcon() {
    const { icon } = this.props
    return !!icon && createElement(icon)
  }

  renderRightIcon() {
    const { rightIcon } = this.props
    return !!rightIcon && createElement(rightIcon)
  }

  renderChildren() {
    const { children, classes } = this.props
    return !!children && (
      <Typography
        color="inherit"
        component="div"
        className={classes.buttonChildren}
      >
        { children }
      </Typography>
    )
  }

  renderDeleteButton() {
    const { classes, onDelete } = this.props
    return !!onDelete && (
      <IconButton className={classes.deleteButton} onClick={onDelete} tabIndex="-1">
        <CloseIcon className={classes.deleteButtonIcon} />
      </IconButton>
    )
  }

  renderButton() {
    const {
      classes, round, icon, rightIcon, disablePadding, pending,
      className, clickable, active, onDelete, fullWidth, color, theme, ...rest
    } = this.props

    const cx = classNames(classes.button, className, {
      [classes.buttonLeftSpace]: !!icon || pending,
      [classes.buttonRightSpace]: !!rightIcon,
      [classes.buttonClickable]: clickable,
      [classes.buttonNoPadding]: disablePadding,
      [classes.buttonRound]: round,
    })

    return (
      <ButtonBase
        {...rest}
        className={cx}
        tabIndex="-1"
        disableRipple={!clickable}
      >
        { this.renderPending() }
        { this.renderIcon() }
        { this.renderChildren() }
        { this.renderRightIcon() }
      </ButtonBase>
    )
  }

  render() {
    const { classes, color, active, fullWidth } = this.props
    const cx = classNames(classes.root, classes[color], {
      [classes.rootActive]: active,
      [classes.rootFullWidth]: fullWidth,
    })
    return (
      <div className={cx}>
        { this.renderButton() }
        { this.renderDeleteButton() }
      </div>
    )
  }
}
